import { useEffect, useState } from 'react';
import { SplashScreen as NativeSplash } from '@capacitor/splash-screen';
import { BrandLockup } from '@/components/ui/brand-lockup';
import { BRAND } from '@/brand/brand';
import { isNative } from '@/lib/native';

/**
 * The launch experience — the first thing anyone sees, and the only chance to say what this app is.
 *
 * There are TWO splashes on a native app and they have to agree:
 *  1. The NATIVE splash, drawn by iOS/Android from the image in `capacitor.config.ts` before any
 *     JavaScript exists. It cannot be animated and it cannot be skipped.
 *  2. This one, drawn by the app the instant React mounts.
 *
 * They are configured to look the same at rest (same background colour), so the handoff is
 * invisible: the native splash holds, this takes over on the same ground, then animates away. Get
 * this wrong and launch flashes white — the single most common tell of a wrapped web app.
 *
 * `autoHide: false` in capacitor.config.ts is what makes this possible: WE decide when the native
 * splash goes, once React has actually painted, rather than the OS hiding it on a timer while the
 * app is still blank.
 */
export function SplashScreen({ onDone }: { onDone?: () => void }) {
	const [leaving, setLeaving] = useState(false);
	const [gone, setGone] = useState(false);

	useEffect(() => {
		// React has painted; the native splash has done its job and can go.
		if (isNative()) void NativeSplash.hide().catch(() => {});

		// Hold briefly so the brand registers, then leave. Long enough to read, short enough not to
		// be a toll gate — anything past ~1.2s reads as a slow app.
		const holdTimer = setTimeout(() => setLeaving(true), 620);
		const doneTimer = setTimeout(() => {
			setGone(true);
			onDone?.();
		}, 1080);
		return () => {
			clearTimeout(holdTimer);
			clearTimeout(doneTimer);
		};
	}, [onDone]);

	if (gone) return null;

	return (
		<div
			aria-hidden="true"
			className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4"
			style={{
				background: 'var(--brand-bg)',
				color: 'var(--brand-ink)',
				opacity: leaving ? 0 : 1,
				transition: 'opacity 420ms ease',
				pointerEvents: leaving ? 'none' : 'auto',
			}}
		>
			{/* The kit's BrandLockup draws the identity: a geometric monogram derived from the brand
			    name, or the real logo when one is supplied. Hand-drawing a letter in a box here is how
			    an app ends up with two different logos that disagree. */}
			<div style={{ animation: 'splashMark 620ms cubic-bezier(0.2, 0.8, 0.2, 1) both' }}>
				<BrandLockup name={BRAND.name} size="lg" tone="brand" markOnly />
			</div>

			<div className="flex flex-col items-center gap-1 px-8 text-center">
				<p
					className="text-xl font-semibold"
					style={{ fontFamily: 'var(--brand-display)', animation: 'splashRise 620ms 90ms ease-out both' }}
				>
					{BRAND.name}
				</p>
				<p
					className="text-muted-foreground text-sm"
					style={{ animation: 'splashRise 620ms 160ms ease-out both' }}
				>
					{BRAND.tagline}
				</p>
			</div>

			<style>{`
				@keyframes splashMark {
					from { opacity: 0; transform: scale(0.86); }
					to   { opacity: 1; transform: scale(1); }
				}
				@keyframes splashRise {
					from { opacity: 0; transform: translateY(6px); }
					to   { opacity: 1; transform: translateY(0); }
				}
				@media (prefers-reduced-motion: reduce) {
					@keyframes splashMark { from, to { opacity: 1; transform: none; } }
					@keyframes splashRise { from, to { opacity: 1; transform: none; } }
				}
			`}</style>
		</div>
	);
}
